import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const VIDEO = "/assets/mythoros/hero-scrub.mp4";
const POSTER = "/assets/mythoros/poster.jpg";

const STAGES = [
  {
    eyebrow: "Mythoros©",
    title: ["A dedicated system.", "Not another browser tab."],
    sub: "Mythoros©, the Kratos Venture Group Desktop AI Agent — a compact appliance that runs your business logic, on your desk and under your control.",
  },
  {
    eyebrow: "Configured to you",
    title: ["Built around how your", "business actually works."],
    sub: "Your workflows, documents, sales scripts, and service standards — configured into one system.",
  },
  {
    eyebrow: "One appliance",
    title: ["Small on the desk.", "An operating layer behind it."],
    sub: "Automation, voice, CRM sync, and research skills — coordinated from a single dedicated device.",
  },
  {
    eyebrow: "On your terms",
    title: ["Human-controlled.", "Permissioned. Always on."],
    sub: "It acts only within approved scope — and it's there every working day, doing the work.",
  },
];

const stageFor = (p) => (p < 0.28 ? 0 : p < 0.55 ? 1 : p < 0.8 ? 2 : 3);

const DarkBtnPrimary = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium tracking-wide bg-white text-black border border-white hover:bg-[#e6e8ec] transition-all duration-300">
    {children}<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);
const DarkBtnSecondary = ({ href, children, testId }) => (
  <a href={href} data-testid={testId} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium tracking-wide bg-transparent text-white border border-white/40 hover:border-white transition-all duration-300">
    {children}
  </a>
);

function Copy({ stage, animated = true }) {
  return (
    <div className="relative w-full max-w-3xl mx-auto text-center px-6">
      <div className="relative h-[240px] sm:h-[220px]">
        {STAGES.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{
              opacity: animated ? (i === stage ? 1 : 0) : 1,
              transform: animated ? `translateY(${i === stage ? 0 : 14}px)` : "none",
              transition: "opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)",
              pointerEvents: i === stage ? "auto" : "none",
            }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-white/70 mb-4">{s.eyebrow}</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.05] text-white text-balance">
              {s.title[0]}<br />{s.title[1]}
            </h1>
            <p className="mt-5 font-body text-sm md:text-base text-white/75 leading-relaxed max-w-xl mx-auto">{s.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Scroll-scrubbed video hero — pinned/tall section, native video colour + resolution, reversible. */
function ScrubHero() {
  const root = useRef(null);
  const videoRef = useRef(null);
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let raf;
    let lastStage = -1;
    let duration = 0;

    const onMeta = () => { duration = video.duration || 0; };
    video.addEventListener("loadedmetadata", onMeta);
    if (video.readyState >= 1) onMeta();

    const tick = () => {
      const el = root.current;
      if (el && duration > 0 && video.readyState >= 1) {
        const r = el.getBoundingClientRect();
        const total = r.height - window.innerHeight;
        const p = total > 0 ? Math.max(0, Math.min(1, -r.top / total)) : 0;
        const t = p * duration;
        if (!video.seeking && Math.abs(video.currentTime - t) > 0.02) {
          try { video.currentTime = t; } catch (e) {}
        }
        const s = stageFor(p);
        if (s !== lastStage) { lastStage = s; setStage(s); }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); video.removeEventListener("loadedmetadata", onMeta); };
  }, []);

  return (
    <section ref={root} className="relative bg-black" style={{ height: "320vh" }} data-testid="agent-video-hero">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
        <video
          ref={videoRef}
          src={VIDEO}
          poster={POSTER}
          muted
          playsInline
          preload="auto"
          data-testid="agent-hero-video"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.14) 40%, rgba(0,0,0,0.55) 100%)" }}
          aria-hidden
        />

        <div className="absolute inset-0 flex items-center justify-center">
          <Copy stage={stage} />
        </div>

        <div className="absolute bottom-10 inset-x-0 flex flex-col items-center gap-6 px-6">
          <div className="flex flex-wrap gap-4 justify-center">
            <DarkBtnPrimary to="/contact" testId="agent-hero-cta">Book a Consultation</DarkBtnPrimary>
            <DarkBtnSecondary href="#capabilities" testId="agent-hero-capabilities">See capabilities</DarkBtnSecondary>
          </div>
          <div className="flex items-center gap-3">
            {STAGES.map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all duration-500 ${i === stage ? "w-8 bg-white" : "w-4 bg-white/25"}`} />
            ))}
          </div>
        </div>

        <div className="absolute left-6 md:left-12 bottom-10 hidden md:flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">Scroll to explore</span>
        </div>
      </div>
    </section>
  );
}

/* Reduced motion: static poster + first-stage copy. */
function StaticHero() {
  return (
    <section className="relative bg-black overflow-hidden" data-testid="agent-video-hero">
      <div className="relative min-h-[70vh] w-full overflow-hidden flex items-center justify-center">
        <img src={POSTER} alt="Mythoros© — Kratos Venture Group Desktop AI Agent" data-testid="agent-hero-poster" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.6))" }} aria-hidden />
        <div className="relative py-24">
          <Copy stage={0} animated={false} />
          <div className="mt-8 flex justify-center">
            <DarkBtnPrimary to="/contact" testId="agent-hero-cta">Book a Consultation</DarkBtnPrimary>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AgentVideoHero() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);
  if (reduced) return <StaticHero />;
  return <ScrubHero />;
}
